var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import CopyIcon from '@returnmates/ui-core/src/components/imageComponents/flyoutSidebar/CopyIcon';
import clsx from 'clsx';
import { memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import InputMask from 'react-input-mask';
import useStyles from './styles';
var invalidChars = ['-', '+', 'e', 'E'];
var Input = function (_a) {
    var _b, _c, _d;
    var StartIcon = _a.startIcon, EndIcon = _a.endIcon, disabled = _a.disabled, multiline = _a.multiline, placeholder = _a.placeholder, type = _a.type, value = _a.value, error = _a.error, success = _a.success, name = _a.name, onChange = _a.onChange, onClick = _a.onClick, id = _a.id, _e = _a.classes, classes = _e === void 0 ? {} : _e, fullWidth = _a.fullWidth, rows = _a.rows, debounce = _a.debounce, _f = _a.timeout, timeout = _f === void 0 ? 300 : _f, classNameProps = _a.className, maxMin = _a.maxMin, decimals = _a.decimals, inputClassName = _a.inputClassName, onFocus = _a.onFocus, onBlur = _a.onBlur, inputRef = _a.inputRef, _g = _a.autoFocus, autoFocus = _g === void 0 ? false : _g, regexp = _a.regexp, onKeyDown = _a.onKeyDown, isUnfocusable = _a.isUnfocusable, helperText = _a.helperText, noBorder = _a.noBorder, pattern = _a.pattern, max = _a.max, min = _a.min, onSubmit = _a.onSubmit, label = _a.label, noStartIconBorder = _a.noStartIconBorder, variant = _a.variant, selectTextOnFocus = _a.selectTextOnFocus, copyToClipboard = _a.copyToClipboard;
    var styles = useStyles({ variant: variant });
    var _h = useState(value), localValue = _h[0], setLocalValue = _h[1];
    var timerRef = useRef();
    var handleOnChange = useCallback(function (event) {
        var _a;
        if (regexp && !new RegExp(regexp).test(event.target.value)) {
            return;
        }
        if (onChange && !disabled) {
            var newValue_1 = event.target.value;
            if (type === 'number') {
                if (max && max < +newValue_1) {
                    return;
                }
                if (newValue_1.length && min && min > +newValue_1) {
                    return;
                }
                if (decimals && !isNaN(Number(decimals)) && Number(decimals) > 0) {
                    var number = newValue_1.split('.');
                    if (((_a = number === null || number === void 0 ? void 0 : number[1]) === null || _a === void 0 ? void 0 : _a.length) > decimals) {
                        newValue_1 = parseFloat(newValue_1).toFixed(decimals);
                    }
                }
            }
            if (!debounce) {
                onChange(newValue_1);
            }
            else {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                }
                timerRef.current = +setTimeout(function () {
                    onChange(newValue_1);
                }, timeout);
            }
            setLocalValue(newValue_1);
        }
    }, [onChange, debounce, timeout, type, decimals, regexp, max, min, disabled]);
    var handleOnBlur = useCallback(function (event) {
        var _a;
        if (onBlur) {
            onBlur();
        }
        if (type === 'number') {
            var newValue = event.target.value;
            if (decimals && !isNaN(Number(decimals)) && Number(decimals) > 0) {
                var number = newValue.split('.') || [];
                if (((_a = number === null || number === void 0 ? void 0 : number[1]) === null || _a === void 0 ? void 0 : _a.length) < decimals) {
                    newValue = parseFloat(newValue).toFixed(decimals);
                }
            }
            var numValue = Number(newValue);
            if (maxMin === null || maxMin === void 0 ? void 0 : maxMin.max) {
                if (numValue > maxMin.max) {
                    newValue = maxMin.max;
                }
            }
            if (maxMin === null || maxMin === void 0 ? void 0 : maxMin.min) {
                if (numValue < maxMin.min) {
                    newValue = maxMin.min;
                }
            }
            onChange === null || onChange === void 0 ? void 0 : onChange(newValue);
            setLocalValue(newValue);
        }
    }, [onBlur, type, decimals, maxMin, onChange]);
    var handleKeyDown = useCallback(function (event) {
        if (type === 'number' && invalidChars.includes(event.key)) {
            event.preventDefault();
        }
        if (onSubmit && event.keyCode === 13) {
            onSubmit(event);
        }
        if (onKeyDown) {
            onKeyDown(event);
        }
    }, [onKeyDown, onSubmit, type]);
    var handleOnFocus = useCallback(function (event) {
        event.target.select();
    }, []);
    useEffect(function () {
        setLocalValue(value);
    }, [value]);
    var maskType = useMemo(function () {
        if (type === 'number' || type === 'tel') {
            return 'tel';
        }
        return 'text';
    }, [type]);
    var textField = (_jsx(TextField, { autoFocus: autoFocus, helperText: error && helperText ? (_jsx("div", __assign({ className: clsx(styles.errorHandler, classes.errorText) }, { children: helperText }))) : (''), label: label, disabled: disabled, multiline: multiline, placeholder: placeholder, type: type, value: localValue, error: error, onFocus: selectTextOnFocus ? handleOnFocus : undefined, name: name, onChange: handleOnChange, onClick: onClick, onBlur: handleOnBlur, id: id, fullWidth: fullWidth, variant: "outlined", className: clsx(classNameProps, classes.textField, styles.textField), rows: rows, InputProps: {
            className: "".concat(classes.input, " ").concat(styles.input, " ").concat(inputClassName, " ").concat(success ? styles.inputSuccessOutline : '', " ").concat(noBorder ? styles.noBorder : '', " ").concat(error ? styles.inputError : '', " ").concat(multiline ? styles.multiline : ''),
            startAdornment: StartIcon ? (_jsxs(InputAdornment, __assign({ className: styles.beforeIcon, position: "start" }, { children: [_jsx(StartIcon, {}), !noStartIconBorder ? _jsx("div", { className: styles.border }) : null] }))) : null,
            endAdornment: EndIcon ? (_jsx(InputAdornment, __assign({ position: "end" }, { children: _jsx(EndIcon, {}) }))) : null,
            classes: {
                notchedOutline: clsx(classes.inputNotchedOutline, (_b = {},
                    _b[styles.inputNotchedOutline] = !error,
                    _b)),
                focused: clsx(classes.focused),
            },
            readOnly: disabled,
        }, inputProps: __assign(__assign({ ref: inputRef, autoComplete: 'chrome-off' }, (maxMin || {})), { className: "".concat(classes.outlined, " ").concat(styles.outlined), onFocus: onFocus, onKeyDown: handleKeyDown, disabled: isUnfocusable }) }));
    return pattern ? (_jsx(InputMask, __assign({ mask: pattern, 
        // @ts-ignore
        maskChar: "", 
        // @ts-ignore
        formatChars: {
            N: '[0-9]',
            S: '[A-Za-z]',
            C: '[A-Fa-f0-9]',
            '*': '[A-Za-z0-9]',
        }, autoFocus: autoFocus, helperText: helperText && (error || success) ? (_jsx("div", __assign({ className: clsx((_c = {},
                _c[styles.errorHandler] = error,
                _c[styles.successHandler] = !error,
                _c[(classes === null || classes === void 0 ? void 0 : classes.errorText) || ''] = error && (classes === null || classes === void 0 ? void 0 : classes.errorText),
                _c)) }, { children: helperText }))) : (''), value: localValue, disabled: disabled, multiline: multiline, placeholder: placeholder, type: maskType, error: error, name: name, onChange: handleOnChange, onClick: onClick, onBlur: handleOnBlur, id: id, label: label, fullWidth: fullWidth, variant: "outlined", className: clsx(classNameProps, classes.textField, styles.textField), rows: rows, InputProps: {
            className: "".concat(classes.input, " ").concat(styles.input, " ").concat(inputClassName, " ").concat(success ? styles.inputSuccessOutline : '', " ").concat(noBorder ? styles.noBorder : '', " ").concat(error ? styles.inputError : '', " ").concat(multiline ? styles.multiline : ''),
            startAdornment: StartIcon ? (_jsxs(InputAdornment, __assign({ className: styles.beforeIcon, position: "start" }, { children: [_jsx(StartIcon, {}), !noStartIconBorder ? _jsx("div", { className: styles.border }) : null] }))) : null,
            endAdornment: EndIcon ? (_jsx(InputAdornment, __assign({ position: "end" }, { children: _jsx(EndIcon, {}) }))) : null,
            classes: {
                notchedOutline: clsx(classes.inputNotchedOutline, (_d = {},
                    _d[styles.inputNotchedOutline] = !error,
                    _d)),
                focused: clsx(classes.focused),
            },
            readOnly: disabled,
        }, inputProps: __assign(__assign({ ref: inputRef, autoComplete: 'chrome-off' }, (maxMin || {})), { className: "".concat(classes.outlined, " ").concat(styles.outlined), onFocus: onFocus, onKeyDown: handleKeyDown }) }, { children: function (inputProps) { return _jsx(TextField, __assign({}, inputProps)); } }))) : localValue && copyToClipboard ? (_jsxs("div", __assign({ className: styles.inputContainer }, { children: [textField, _jsx(CopyIcon, { className: styles.copyIcon, onClick: copyToClipboard(name || 'Value', localValue) })] }))) : (textField);
};
export default memo(Input);
//# sourceMappingURL=index.js.map